import type { Routine } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CopyPlus } from 'lucide-react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/exercise/Chip';
import { ExerciseThumb } from '@/components/exercise/ExerciseThumb';
import { prescriptionLine, templateMeta } from '@/components/routine/templateFormat';
import { describeError } from '@/lib/api';
import { SAVED, showToast } from '@/lib/toast';
import { useForkRoutine, useRoutine, useRoutineTemplates } from '@/lib/queries/routines';

/**
 * Detalle de una plantilla. Es una rutina pública de la cuenta de sistema, así
 * que se lee con `GET /v1/routines/{id}`; los datos de la tarjeta (nivel,
 * sesiones por semana) salen del catálogo, que ya está en caché.
 */
export const Route = createFileRoute('/_app/routines/templates/$id')({
  component: TemplateDetailPage,
});

function TemplateDetailPage() {
  const { id } = Route.useParams();
  const routine = useRoutine(id);
  const templates = useRoutineTemplates();
  const template = templates.data?.find((item) => item.id === id);

  return (
    <>
      <PageHeader
        title={routine.data?.name ?? template?.name ?? 'Plantilla'}
        subtitle={template ? templateMeta(template) : undefined}
        action={
          <Link
            to="/routines"
            search={{ archived: false }}
            aria-label="Volver a las rutinas"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {routine.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando la plantilla…</span>
        </div>
      ) : routine.isError ? (
        <ErrorBanner
          title="No hemos podido cargar la plantilla"
          error={routine.error}
          onRetry={() => void routine.refetch()}
        />
      ) : (
        <TemplateDetail routine={routine.data} />
      )}
    </>
  );
}

function TemplateDetail({ routine }: { routine: Routine }) {
  const exercises = routine.days.reduce((total, day) => total + day.exercises.length, 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-1.5">
        <Badge tone="brand">Plantilla</Badge>
        <Badge>
          {routine.days.length} {routine.days.length === 1 ? 'día' : 'días'}
        </Badge>
        <Badge>
          {exercises} {exercises === 1 ? 'ejercicio' : 'ejercicios'}
        </Badge>
      </div>

      {routine.description !== null && routine.description !== '' ? (
        <p className="text-sm whitespace-pre-line text-ink-muted">{routine.description}</p>
      ) : null}

      <UseTemplate routineId={routine.id} />

      <div className="flex flex-col gap-4">
        {routine.days.map((day) => (
          <section key={day.id} className="rounded-2xl border border-line bg-surface-raised p-4">
            <header className="mb-3">
              <h2 className="text-base font-semibold">{day.name}</h2>
              {day.notes !== null && day.notes !== '' ? (
                <p className="mt-1 text-sm text-ink-muted">{day.notes}</p>
              ) : null}
            </header>
            <ul className="flex flex-col gap-2">
              {day.exercises.map((item) => (
                <li key={item.id}>
                  {/* La fila entera lleva a la ficha: el nombre solo era una zona táctil
                      de una línea de texto. */}
                  <Link
                    to="/exercises/$id"
                    params={{ id: item.exercise.id }}
                    className="-mx-2 flex min-h-11 items-center gap-3 rounded-xl px-2 py-1 transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
                  >
                    <ExerciseThumb
                      media={item.exercise.media}
                      name={item.exercise.nameEs}
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium text-ink">
                        {item.exercise.nameEs}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-muted">
                        {prescriptionLine(item)}
                      </span>
                      {item.supersetGroup !== null ? (
                        <span className="mt-0.5 block text-xs text-brand">
                          Superserie {item.supersetGroup + 1}
                        </span>
                      ) : null}
                      {item.notes !== null && item.notes !== '' ? (
                        <span className="mt-0.5 block text-xs text-ink-faint">{item.notes}</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <UseTemplate routineId={routine.id} />
    </div>
  );
}

/**
 * Copia la plantilla a «Mis rutinas» y abre la copia, que ya es del usuario: se
 * puede editar y empezar como cualquier otra.
 */
function UseTemplate({ routineId }: { routineId: string }) {
  const navigate = useNavigate();
  const fork = useForkRoutine();

  return (
    <section className="flex flex-col gap-2">
      <Button
        size="lg"
        block
        data-tour="use-template"
        loading={fork.isPending}
        icon={<CopyPlus className="size-5" />}
        onClick={() =>
          fork.mutate(routineId, {
            onSuccess: (copy) => {
              showToast(SAVED.templateCopied);
              void navigate({ to: '/routines/$id', params: { id: copy.id } });
            },
          })
        }
      >
        Usar esta plantilla
      </Button>
      <p className="text-center text-xs text-ink-faint">
        Se copia a «Mis rutinas» y podrás cambiar días, ejercicios y series.
      </p>
      {fork.isError ? (
        <p role="alert" className="text-sm text-danger">
          {describeError(fork.error)}
        </p>
      ) : null}
    </section>
  );
}
